/**
 * Medical History Management Component
 * Allows patients to view, add, edit, and manage their medical history
 */
import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  Button,
  Card,
  CardContent,
  CardActions,
  Avatar,
  Chip,
  Stack,
  Divider,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemSecondaryAction,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  Tooltip,
  Badge,
  LinearProgress,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination
} from '@mui/material';
import {
  History as HistoryIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  MedicalServices as MedicalIcon,
  LocalHospital as HospitalIcon,
  Assessment as AssessmentIcon,
  Timeline as TimelineIcon,
  Warning as WarningIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Save as SaveIcon,
  ExpandMore as ExpandMoreIcon,
  DateRange as DateIcon,
  Person as PersonIcon,
  Description as DescriptionIcon,
  LocalPharmacy as PharmacyIcon,
  Favorite as HeartIcon,
  Psychology as PsychologyIcon,
  Accessibility as AccessibilityIcon,
  Sports as SportsIcon,
  Restaurant as FoodIcon,
  SmokingRooms as SmokingIcon,
  LocalDrink as DrinkIcon,
  Bedtime as SleepIcon,
  FitnessCenter as FitnessIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { toast } from 'react-toastify';
import { format } from 'date-fns';

// API Services
import { medicalHistoryApi } from '../../services/api/patientManagement';

const MedicalHistory = () => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [medicalHistory, setMedicalHistory] = useState([]);
  const [filteredHistory, setFilteredHistory] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  
  // Dialog states
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedHistory, setSelectedHistory] = useState(null);
  
  // Form states
  const [historyForm, setHistoryForm] = useState({
    condition: '',
    diagnosis_date: null,
    treatment: '',
    status: 'active',
    severity: 'mild',
    doctor_name: '',
    hospital_name: '',
    notes: '',
    allergies: '',
    family_history: '',
    lifestyle_factors: ''
  });

  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => {
    loadMedicalHistory();
  }, []);

  useEffect(() => {
    filterHistory();
  }, [medicalHistory, searchTerm, filterStatus]);

  const loadMedicalHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await medicalHistoryApi.getMedicalHistory();
      setMedicalHistory(response);
    } catch (err) {
      console.error('Error loading medical history:', err);
      setError('Failed to load medical history. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const filterHistory = () => {
    let filtered = medicalHistory;

    // Filter by search term
    if (searchTerm) {
      filtered = filtered.filter(history =>
        history.condition.toLowerCase().includes(searchTerm.toLowerCase()) ||
        history.treatment.toLowerCase().includes(searchTerm.toLowerCase()) ||
        history.doctor_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Filter by status
    if (filterStatus !== 'all') {
      filtered = filtered.filter(history => history.status === filterStatus);
    }

    setFilteredHistory(filtered);
  };

  const handleAddHistory = async () => {
    try {
      setLoading(true);
      await medicalHistoryApi.createMedicalHistory(historyForm);
      toast.success('Medical history entry added successfully!');
      setShowAddDialog(false);
      setHistoryForm({
        condition: '',
        diagnosis_date: null,
        treatment: '',
        status: 'active',
        severity: 'mild',
        doctor_name: '',
        hospital_name: '',
        notes: '',
        allergies: '',
        family_history: '',
        lifestyle_factors: ''
      });
      await loadMedicalHistory();
    } catch (err) {
      console.error('Error adding medical history:', err);
      toast.error('Failed to add medical history entry. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleEditHistory = async () => {
    try {
      setLoading(true);
      await medicalHistoryApi.updateMedicalHistory(selectedHistory.id, historyForm);
      toast.success('Medical history entry updated successfully!');
      setShowEditDialog(false);
      setSelectedHistory(null);
      await loadMedicalHistory();
    } catch (err) {
      console.error('Error updating medical history:', err);
      toast.error('Failed to update medical history entry. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteHistory = async () => {
    try {
      setLoading(true);
      await medicalHistoryApi.deleteMedicalHistory(selectedHistory.id);
      toast.success('Medical history entry deleted successfully!');
      setShowDeleteDialog(false);
      setSelectedHistory(null);
      await loadMedicalHistory();
    } catch (err) {
      console.error('Error deleting medical history:', err);
      toast.error('Failed to delete medical history entry. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const openEditDialog = (history) => {
    setSelectedHistory(history);
    setHistoryForm({
      condition: history.condition || '',
      diagnosis_date: history.diagnosis_date ? new Date(history.diagnosis_date) : null,
      treatment: history.treatment || '',
      status: history.status || 'active',
      severity: history.severity || 'mild',
      doctor_name: history.doctor_name || '',
      hospital_name: history.hospital_name || '',
      notes: history.notes || '',
      allergies: history.allergies || '',
      family_history: history.family_history || '',
      lifestyle_factors: history.lifestyle_factors || ''
    });
    setShowEditDialog(true);
  };

  const openDeleteDialog = (history) => {
    setSelectedHistory(history);
    setShowDeleteDialog(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return 'error';
      case 'resolved': return 'success';
      case 'chronic': return 'warning';
      case 'monitoring': return 'info';
      default: return 'default';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'mild': return 'success';
      case 'moderate': return 'warning';
      case 'severe': return 'error';
      case 'critical': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active': return <WarningIcon />;
      case 'resolved': return <CheckIcon />;
      case 'chronic': return <TimelineIcon />;
      case 'monitoring': return <AssessmentIcon />;
      default: return <MedicalIcon />;
    }
  };

  const StatCard = ({ title, value, icon, color, onClick }) => (
    <Card 
      sx={{ 
        height: '100%', 
        cursor: onClick ? 'pointer' : 'default',
        transition: 'transform 0.2s ease',
        '&:hover': onClick ? { transform: 'translateY(-4px)' } : {}
      }}
      onClick={onClick}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Avatar sx={{ bgcolor: color, mr: 2 }}>
            {icon}
          </Avatar>
          <Box>
            <Typography variant="h6" component="div">
              {value}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {title}
            </Typography>
          </Box>
        </Box>
      </CardContent>
    </Card>
  );

  const HistoryCard = ({ history, onEdit, onDelete }) => (
    <Card sx={{ mb: 2 }}>
      <CardContent>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h6" component="div">
              {history.condition}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {history.doctor_name && `Dr. ${history.doctor_name}`}
              {history.hospital_name && ` • ${history.hospital_name}`}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Chip
              label={history.status}
              color={getStatusColor(history.status)}
              size="small"
              icon={getStatusIcon(history.status)}
            />
            <Chip
              label={history.severity}
              color={getSeverityColor(history.severity)}
              size="small"
            />
          </Box>
        </Box>
        
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">
              <strong>Diagnosis Date:</strong> {history.diagnosis_date ? format(new Date(history.diagnosis_date), 'MMM d, yyyy') : 'Not specified'}
            </Typography>
          </Grid>
          <Grid item xs={12} sm={6}>
            <Typography variant="body2" color="text.secondary">
              <strong>Treatment:</strong> {history.treatment || 'Not specified'}
            </Typography>
          </Grid>
          {history.notes && (
            <Grid item xs={12}>
              <Typography variant="body2" color="text.secondary">
                <strong>Notes:</strong> {history.notes}
              </Typography>
            </Grid>
          )}
        </Grid>
      </CardContent>
      <CardActions>
        <Button size="small" startIcon={<EditIcon />} onClick={() => onEdit(history)}>
          Edit
        </Button>
        <Button size="small" startIcon={<DeleteIcon />} color="error" onClick={() => onDelete(history)}>
          Delete
        </Button>
      </CardActions>
    </Card>
  );

  const HistoryForm = ({ title, onSave, onCancel, loading }) => (
    <Box>
      <Typography variant="h6" gutterBottom>
        {title}
      </Typography>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Condition"
            value={historyForm.condition}
            onChange={(e) => setHistoryForm({ ...historyForm, condition: e.target.value })}
            required
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              label="Diagnosis Date"
              value={historyForm.diagnosis_date}
              onChange={(date) => setHistoryForm({ ...historyForm, diagnosis_date: date })}
              slotProps={{
                textField: {
                  fullWidth: true
                }
              }}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Treatment"
            value={historyForm.treatment}
            onChange={(e) => setHistoryForm({ ...historyForm, treatment: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              value={historyForm.status}
              onChange={(e) => setHistoryForm({ ...historyForm, status: e.target.value })}
              label="Status"
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="resolved">Resolved</MenuItem>
              <MenuItem value="chronic">Chronic</MenuItem>
              <MenuItem value="monitoring">Monitoring</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <FormControl fullWidth>
            <InputLabel>Severity</InputLabel>
            <Select
              value={historyForm.severity}
              onChange={(e) => setHistoryForm({ ...historyForm, severity: e.target.value })}
              label="Severity"
            >
              <MenuItem value="mild">Mild</MenuItem>
              <MenuItem value="moderate">Moderate</MenuItem>
              <MenuItem value="severe">Severe</MenuItem>
              <MenuItem value="critical">Critical</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Doctor Name"
            value={historyForm.doctor_name}
            onChange={(e) => setHistoryForm({ ...historyForm, doctor_name: e.target.value })}
          />
        </Grid>
        <Grid item xs={12} sm={6}>
          <TextField
            fullWidth
            label="Hospital/Clinic"
            value={historyForm.hospital_name}
            onChange={(e) => setHistoryForm({ ...historyForm, hospital_name: e.target.value })}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={3}
            label="Notes"
            value={historyForm.notes}
            onChange={(e) => setHistoryForm({ ...historyForm, notes: e.target.value })}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Allergies"
            value={historyForm.allergies}
            onChange={(e) => setHistoryForm({ ...historyForm, allergies: e.target.value })}
            placeholder="List any known allergies..."
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Family History"
            value={historyForm.family_history}
            onChange={(e) => setHistoryForm({ ...historyForm, family_history: e.target.value })}
            placeholder="Relevant family medical history..."
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            fullWidth
            multiline
            rows={2}
            label="Lifestyle Factors"
            value={historyForm.lifestyle_factors}
            onChange={(e) => setHistoryForm({ ...historyForm, lifestyle_factors: e.target.value })}
            placeholder="Diet, exercise, smoking, alcohol consumption, etc."
          />
        </Grid>
      </Grid>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
        <Button onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button onClick={onSave} variant="contained" disabled={loading}>
          {loading ? <CircularProgress size={20} /> : 'Save'}
        </Button>
      </Box>
    </Box>
  );

  if (loading && medicalHistory.length === 0) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: '1200px', margin: '0 auto' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          Medical History
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setShowAddDialog(true)}
        >
          Add Entry
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Conditions"
            value={medicalHistory.length}
            icon={<MedicalIcon />}
            color="primary.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Active Conditions"
            value={medicalHistory.filter(h => h.status === 'active').length}
            icon={<WarningIcon />}
            color="error.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Resolved"
            value={medicalHistory.filter(h => h.status === 'resolved').length}
            icon={<CheckIcon />}
            color="success.main"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Chronic"
            value={medicalHistory.filter(h => h.status === 'chronic').length}
            icon={<TimelineIcon />}
            color="warning.main"
          />
        </Grid>
      </Grid>

      {/* Filters */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} sm={6} md={4}>
            <TextField
              fullWidth
              label="Search conditions"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4}>
            <FormControl fullWidth>
              <InputLabel>Filter by Status</InputLabel>
              <Select
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
                label="Filter by Status"
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="resolved">Resolved</MenuItem>
                <MenuItem value="chronic">Chronic</MenuItem>
                <MenuItem value="monitoring">Monitoring</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={12} md={4}>
            <Typography variant="body2" color="text.secondary">
              Showing {filteredHistory.length} of {medicalHistory.length} entries
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* Medical History List */}
      {filteredHistory.length === 0 ? (
        <Paper elevation={2} sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <MedicalIcon sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            No medical history entries found
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {searchTerm || filterStatus !== 'all' 
              ? 'Try adjusting your search or filter criteria.'
              : 'Start by adding your first medical history entry.'
            }
          </Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setShowAddDialog(true)}
          >
            Add First Entry
          </Button>
        </Paper>
      ) : (
        <Stack spacing={2}>
          {filteredHistory
            .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
            .map((history) => (
              <HistoryCard
                key={history.id}
                history={history}
                onEdit={openEditDialog}
                onDelete={openDeleteDialog}
              />
            ))}
        </Stack>
      )}

      {/* Pagination */}
      {filteredHistory.length > rowsPerPage && (
        <TablePagination
          component="div"
          count={filteredHistory.length}
          page={page}
          onPageChange={(event, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(event) => {
            setRowsPerPage(parseInt(event.target.value, 10));
            setPage(0);
          }}
          rowsPerPageOptions={[5, 10, 25]}
        />
      )}

      {/* Add History Dialog */}
      <Dialog open={showAddDialog} onClose={() => setShowAddDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add Medical History Entry</DialogTitle>
        <DialogContent>
          <HistoryForm
            title="New Medical History Entry"
            onSave={handleAddHistory}
            onCancel={() => setShowAddDialog(false)}
            loading={loading}
          />
        </DialogContent>
      </Dialog>

      {/* Edit History Dialog */}
      <Dialog open={showEditDialog} onClose={() => setShowEditDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Medical History Entry</DialogTitle>
        <DialogContent>
          <HistoryForm
            title="Edit Medical History Entry"
            onSave={handleEditHistory}
            onCancel={() => setShowEditDialog(false)}
            loading={loading}
          />
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onClose={() => setShowDeleteDialog(false)}>
        <DialogTitle>Delete Medical History Entry</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to delete this medical history entry?
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <strong>Condition:</strong> {selectedHistory?.condition}
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }}>
            This action cannot be undone.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteDialog(false)}>Cancel</Button>
          <Button onClick={handleDeleteHistory} color="error" variant="contained" disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default MedicalHistory;
